<template>
  <Teleport :to="teleportTarget">
    <Transition name="cc-fade">
      <div v-if="modelValue" class="cc-mask" @click.self="onClose">
        <div class="cc-wrap">
          <Transition name="cc-pop">
            <dialog v-if="modelValue" class="cc-panel" open :aria-label="copy.title">
              <header class="cc-head">
                <h2 class="cc-title">{{ copy.title }}</h2>
                <button type="button" class="cc-close" :aria-label="copy.close" @click="onClose">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.8" />
                  </svg>
                </button>
              </header>

              <p class="cc-tip">
                <span>{{ copy.tipPrefix }}</span>
                <span class="cc-tip-targets">
                  <template v-for="(target, idx) in targets" :key="`${target}-${idx}`">
                    <strong
                      class="cc-tip-target"
                      :class="{ 'cc-tip-target--done': idx < points.length }"
                    >
                      {{ formatTarget(target, idx) }}
                    </strong>
                  </template>
                </span>
              </p>

              <div class="cc-stage" :class="{ 'cc-stage--loading': loading }">
                <div v-if="loading" class="cc-loading">{{ copy.loading }}</div>
                <!--
                  标记点必须与 img 共用同一坐标原点：原先点挂在 .cc-stage 上，而 stage 用 flex 居中，
                  点击坐标却相对 img.getBoundingClientRect()，会导致标记整体偏移（常见为偏左/偏上）。
                -->
                <div v-else-if="imageBase64" class="cc-image-shell">
                  <img
                    ref="imageRef"
                    class="cc-image"
                    :src="imageBase64"
                    :width="imageSize.width"
                    :height="imageSize.height"
                    draggable="false"
                    :alt="copy.stageAlt"
                    @click="onImageClick"
                  />
                  <span
                    v-for="(p, idx) in points"
                    :key="idx"
                    class="cc-point"
                    :style="{ left: `${p.displayX}px`, top: `${p.displayY}px` }"
                  >
                    {{ Number(idx) + 1 }}
                  </span>
                </div>
                <p v-if="errorText" class="cc-error">{{ errorText }}</p>
              </div>

              <footer class="cc-foot">
                <button type="button" class="cc-btn cc-btn--ghost" @click="reload">
                  {{ copy.refresh }}
                </button>
                <button
                  type="button"
                  class="cc-btn cc-btn--ghost"
                  :disabled="points.length === 0"
                  @click="undoPoint"
                >
                  {{ copy.undo }}
                </button>
                <button
                  type="button"
                  class="cc-btn cc-btn--primary"
                  :disabled="!canSubmit"
                  @click="submit"
                >
                  {{ copy.confirm }}
                </button>
              </footer>
            </dialog>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { mergeBaUserTokenHeaders } from '../utils/fetchWithBaUserToken'

interface ClickCaptchaPayload {
  captchaId: string
  captchaInfo: string
}

interface ClickCaptchaCopy {
  title: string
  tipPrefix: string
  refresh: string
  undo: string
  confirm: string
  close: string
  loading: string
  stageAlt: string
  loadError: string
}

interface Point {
  /** 相对原图坐标系的 x */
  x: number
  /** 相对原图坐标系的 y */
  y: number
  /** 展示用坐标（相对图片元素容器） */
  displayX: number
  displayY: number
}

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    /** 获取点选图的后端地址，响应结构 `{ ok, id, text[], base64, width, height }`。 */
    endpoint?: string
    /** 父层提供的 apiBase；留空时使用相对路径。 */
    apiBase?: string
    /**
     * 由宿主通过 i18n 注入的完整文案；传入时优先使用，不再依赖内置 `locale` 映射。
     */
    captchaCopy?: ClickCaptchaCopy | null
    /** 语种文案（仅在内置文案回退时使用：zh* / 其它）。 */
    locale?: string
    /** teleport 目标，默认 body。 */
    teleportTarget?: string
  }>(),
  {
    endpoint: '/api/auth/click-captcha',
    apiBase: '',
    captchaCopy: null,
    locale: 'zh-CN',
    teleportTarget: 'body',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  resolved: [payload: ClickCaptchaPayload]
  cancelled: []
}>()

const loading = ref(false)
const errorText = ref('')
const imageBase64 = ref('')
const imageSize = ref<{ width: number; height: number }>({ width: 300, height: 180 })
const targets = ref<string[]>([])
const points = ref<Point[]>([])
const captchaId = ref('')
const imageRef = ref<HTMLImageElement | null>(null)

const copyByLocale: Record<string, ClickCaptchaCopy> = {
  'zh-CN': {
    title: '人机验证',
    tipPrefix: '请依次点击：',
    refresh: '换一张',
    undo: '撤销',
    confirm: '提交',
    close: '关闭人机验证',
    loading: '加载中...',
    stageAlt: '依次点击标注目标',
    loadError: '加载失败，请点击“换一张”重试',
  },
  en: {
    title: 'Human verification',
    tipPrefix: 'Click in order:',
    refresh: 'Refresh',
    undo: 'Undo',
    confirm: 'Confirm',
    close: 'Close captcha dialog',
    loading: 'Loading...',
    stageAlt: 'Click the highlighted targets in order',
    loadError: 'Load failed, please refresh',
  },
}

const copy = computed<ClickCaptchaCopy>(() => {
  if (props.captchaCopy) {
    return props.captchaCopy
  }
  return String(props.locale).startsWith('zh') ? copyByLocale['zh-CN']! : copyByLocale.en!
})

const canSubmit = computed(() => {
  if (loading.value) return false
  if (!imageBase64.value) return false
  return points.value.length > 0 && points.value.length === targets.value.length
})

watch(
  () => props.modelValue,
  (opened: boolean) => {
    if (!opened) return
    points.value = []
    errorText.value = ''
    void reload()
  },
  { immediate: true },
)

/**
 * 生成一个本地随机验证码 id（用于后端 `md5(id)` 存储 key）。
 */
function generateCaptchaId(): string {
  const bytes = new Uint8Array(12)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * 从后端获取一张新的点选验证码图片。
 */
async function reload(): Promise<void> {
  points.value = []
  errorText.value = ''
  loading.value = true
  captchaId.value = generateCaptchaId()
  try {
    const url = `${(props.apiBase || '').replace(/\/$/, '')}${props.endpoint}?id=${encodeURIComponent(captchaId.value)}`
    const res = await fetch(url, {
      method: 'GET',
      headers: mergeBaUserTokenHeaders({ accept: 'application/json' }),
      credentials: 'include',
    })
    if (!res.ok) {
      errorText.value = copy.value.loadError
      return
    }
    const data = (await res.json()) as {
      ok?: boolean
      id?: string
      text?: string[]
      base64?: string
      width?: number
      height?: number
    }
    if (!data.base64) {
      errorText.value = copy.value.loadError
      return
    }
    imageBase64.value = data.base64
    imageSize.value = {
      width: Number(data.width) || 300,
      height: Number(data.height) || 180,
    }
    targets.value = Array.isArray(data.text) ? data.text : []
  } catch {
    errorText.value = copy.value.loadError
  } finally {
    loading.value = false
  }
}

/**
 * 格式化待点击目标的展示文本（去除前后尖括号、保留中文描述）。
 *
 * @param raw - 后端返回的目标字符串
 * @param idx - 序号（从 0 起）
 */
function formatTarget(raw: string, idx: number): string {
  const cleaned = String(raw || '').replaceAll(/^<|>$/g, '')
  return `${idx + 1}. ${cleaned}`
}

/**
 * 用户在图像上点击登记一个点。
 *
 * @param ev - 鼠标事件
 */
/**
 * 读取当前图片在视口中的 CSS 像素框（用于点击映射；与 `.cc-image-shell` 包裹尺寸一致）。
 *
 * @param img - 验证码图片元素
 */
function getImageCssRect(img: HTMLImageElement): DOMRect {
  return img.getBoundingClientRect()
}

function onImageClick(ev: MouseEvent): void {
  if (points.value.length >= targets.value.length) return
  const img = imageRef.value
  if (!img) return
  const rect = getImageCssRect(img)
  /** 相对图片可见区域左上角（与标记点 `left/top` 同一坐标系） */
  const displayX = ev.clientX - rect.left
  const displayY = ev.clientY - rect.top
  /** 换算为原图坐标（后端在 `ClickCaptcha::check` 以宽高比例还原）。 */
  const ratioX = imageSize.value.width / rect.width
  const ratioY = imageSize.value.height / rect.height
  const x = Math.round(displayX * ratioX)
  const y = Math.round(displayY * ratioY)
  points.value.push({ x, y, displayX, displayY })
}

/**
 * 撤销最近一次点击。
 */
function undoPoint(): void {
  points.value.pop()
}

/**
 * 提交点选结果给父层。
 *
 * @remarks
 * `captchaInfo` 的格式：`x1,y1-x2,y2-...;width;height`（与后端 `explode(';')` 对齐）。
 */
function submit(): void {
  if (!canSubmit.value) return
  const img = imageRef.value
  const rect = img ? getImageCssRect(img) : null
  const width = rect ? Math.round(rect.width) : imageSize.value.width
  const height = rect ? Math.round(rect.height) : imageSize.value.height
  const xy = points.value.map((p: Point) => `${p.displayX.toFixed(0)},${p.displayY.toFixed(0)}`).join('-')
  const captchaInfo = `${xy};${width};${height}`
  emit('resolved', { captchaId: captchaId.value, captchaInfo })
  emit('update:modelValue', false)
}

/**
 * 关闭弹窗（用户取消）。
 */
function onClose(): void {
  emit('cancelled')
  emit('update:modelValue', false)
}
</script>

<style scoped>
.cc-mask {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgb(2 6 23 / 66%);
  padding: 1rem;
}

.cc-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cc-panel {
  position: relative;
  inset: auto;
  width: min(420px, calc(100vw - 2rem));
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  margin: 0 auto;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  box-shadow: var(--mkt-shadow, 0 18px 60px rgb(15 23 42 / 20%));
  color: var(--mkt-color-text, #0f172a);
}

.cc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cc-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.cc-close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: inherit;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.cc-tip {
  margin: 0.9rem 0 0.55rem;
  font-size: 0.85rem;
  color: var(--mkt-color-muted, #526077);
}

.cc-tip-targets {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.45rem;
  margin-left: 0.35rem;
}

.cc-tip-target {
  display: inline-flex;
  align-items: center;
  min-height: 1.65rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: #0c4a6e;
  color: #fff;
  border: 1px solid #0c4a6e;
}

.cc-tip-target--done {
  opacity: 0.45;
  text-decoration: line-through;
}

.cc-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  border-radius: 10px;
  border: 1px dashed var(--mkt-color-border, #d6deea);
  overflow: hidden;
  background: var(--mkt-color-bg, #f8fafc);
}

/**
 * 与内部图片同宽同高，使绝对定位的序号点以图片左上角为原点（避免 flex 居中带来的偏移）。
 */
.cc-image-shell {
  position: relative;
  display: inline-block;
  max-width: 100%;
  line-height: 0;
  vertical-align: top;
}

.cc-stage--loading {
  color: var(--mkt-color-muted, #526077);
  font-size: 0.85rem;
}

.cc-image {
  display: block;
  max-width: 100%;
  height: auto;
  cursor: crosshair;
  user-select: none;
}

.cc-point {
  position: absolute;
  transform: translate(-50%, -50%);
  min-width: 26px;
  height: 26px;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #1f7ab8;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  display: grid;
  place-items: center;
  pointer-events: none;
  box-shadow: 0 6px 14px rgb(31 122 184 / 35%);
}

.cc-loading {
  font-size: 0.85rem;
  color: var(--mkt-color-muted, #526077);
}

.cc-error {
  margin: 0;
  font-size: 0.82rem;
  color: #b91c1c;
  padding: 0.5rem;
  text-align: center;
}

.cc-foot {
  margin-top: 0.85rem;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
}

.cc-btn {
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid var(--mkt-color-border, #d6deea);
  background: var(--mkt-color-surface, #ffffff);
  color: var(--mkt-color-text, #0f172a);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.86rem;
}

.cc-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.cc-btn--primary {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(135deg, #156f97, #1f7ab8);
}

.cc-btn--ghost {
  background: transparent;
}

.cc-fade-enter-active,
.cc-fade-leave-active {
  transition: opacity 180ms ease;
}

.cc-fade-enter-from,
.cc-fade-leave-to {
  opacity: 0;
}

.cc-pop-enter-active,
.cc-pop-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.cc-pop-enter-from,
.cc-pop-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .cc-fade-enter-active,
  .cc-fade-leave-active,
  .cc-pop-enter-active,
  .cc-pop-leave-active {
    transition: none;
  }
}
</style>
